import React, { useMemo, useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Wrench, PlayCircle, CheckCircle2, AlertTriangle } from "lucide-react";

// Rupiah formatter so + / − deltas render consistently with the rest
// of the Reports surface (no decimals, grouped thousands).
const fmtIDR = (n) =>
  (n < 0 ? "- " : "") +
  "Rp " +
  Math.abs(Math.round(Number(n) || 0)).toLocaleString("en-US");

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/**
 * Commission Repair Tools (Iter-386).
 *
 * Owner-only panel that pairs the existing bulk recompute with a
 * read-only window preview so the owner can see which rows the
 * current formula WOULD rewrite before firing the destructive
 * recompute. Uses the logged-in staff token through `staffApi`,
 * so there is zero copy-paste of tokens.
 *
 * Hidden entirely for non-owner roles.
 */
export default function CommissionRepairTools() {
  const { staff } = useAuth();
  // The API returns `role_detail.is_owner` on `/auth/me`, so route
  // through that; `user.is_owner` is also honoured as a cheap fallback.
  const amOwner = Boolean(
    staff && (staff.is_owner || staff.role_detail?.is_owner),
  );
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const years = useMemo(() => {
    const current = now.getFullYear();
    // Spa opened mid-2024, no closed orders before that.
    const start = 2024;
    const list = [];
    for (let y = current + 1; y >= start; y--) list.push(String(y));
    return list;
  }, [now]);

  const windowBounds = useMemo(() => {
    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    const start = `${y}-${String(m).padStart(2, "0")}-01`;
    const endMonth = m === 12 ? 1 : m + 1;
    const endYear = m === 12 ? y + 1 : y;
    const end = `${endYear}-${String(endMonth).padStart(2, "0")}-01`;
    return { start, end, label: `${MONTHS[m - 1]} ${y}` };
  }, [year, month]);

  if (!amOwner) return null;

  const runPreview = async () => {
    setPreviewing(true);
    setApplied(null);
    try {
      const r = await staffApi.get("/commission-ledger/preview-window", {
        params: { start_iso: windowBounds.start, end_iso: windowBounds.end },
      });
      setPreview(r.data);
      if (r.data.orders_scanned === 0) {
        toast.info("No completed orders in that month.");
      } else if (r.data.orders_with_changes === 0) {
        toast.success("All rows already match the current formula — nothing to fix.");
      } else {
        toast.success(
          `${r.data.orders_with_changes} order${r.data.orders_with_changes === 1 ? "" : "s"} would be corrected.`,
        );
      }
    } catch (err) {
      setPreview(null);
      toast.error(formatApiError(err));
    } finally {
      setPreviewing(false);
    }
  };

  const applyFix = async () => {
    setConfirmOpen(false);
    setApplying(true);
    try {
      const r = await staffApi.post(
        "/commission-ledger/recompute",
        null,
        { params: {
          start_iso: windowBounds.start,
          end_iso: windowBounds.end,
          confirm: true,
        } },
      );
      setApplied(r.data);
      toast.success(
        `Fix applied: ${r.data.orders_touched} order${r.data.orders_touched === 1 ? "" : "s"} corrected.`,
      );
      // Clear the preview so the owner must re-run it before another Apply —
      // avoids an accidental re-click rewriting the same window twice.
      setPreview(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setApplying(false);
    }
  };

  const changedDiffs = (preview?.diffs || []).filter((d) => d.has_change);

  return (
    <Card className="rounded-lg" data-testid="commission-repair-tools">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-amber-50 border border-amber-200 p-2">
            <Wrench className="w-5 h-5 text-amber-700" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold">Commission Repair Tools</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Preview and fix historical commission rows for a whole month in
              one click. Preview is safe to run anytime; Apply Fix rewrites
              ledger rows and requires confirmation.
            </p>
          </div>
          <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
            Owner only
          </Badge>
        </div>

        <div className="grid sm:grid-cols-[200px_160px_1fr] gap-3 items-end">
          <div>
            <Label>Month</Label>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="repair-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={m} value={String(i + 1)} data-testid={`repair-month-${i + 1}`}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger data-testid="repair-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={y} data-testid={`repair-year-${y}`}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={runPreview}
              disabled={previewing || applying}
              data-testid="repair-preview-btn"
            >
              <PlayCircle className="w-4 h-4 mr-1" />
              {previewing ? "Previewing…" : "Preview"}
            </Button>
            {preview && preview.orders_with_changes > 0 && (
              <Button
                onClick={() => setConfirmOpen(true)}
                disabled={applying}
                data-testid="repair-apply-btn"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" />
                {applying ? "Applying…" : "Apply Fix"}
              </Button>
            )}
          </div>
        </div>

        {preview && (
          <div className="space-y-3" data-testid="repair-preview-result">
            <div className="flex flex-wrap gap-4 text-sm">
              <span>
                <strong>{preview.orders_scanned}</strong> closed order{preview.orders_scanned === 1 ? "" : "s"} scanned in {windowBounds.label}
              </span>
              <span className="text-amber-800">
                <strong>{preview.orders_with_changes}</strong> would change
              </span>
              <span className="text-muted-foreground">
                {preview.orders_unchanged} already correct
              </span>
              <span>
                Net delta:{" "}
                <strong
                  className={preview.total_delta < 0 ? "text-rose-700" : "text-emerald-700"}
                  data-testid="repair-total-delta"
                >
                  {fmtIDR(preview.total_delta)}
                </strong>
              </span>
            </div>

            {changedDiffs.length === 0 ? (
              <p className="text-sm text-emerald-700 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Nothing to fix — every row already matches the current formula.
              </p>
            ) : (
              <div className="rounded-md border border-border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order #</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead className="text-right">Current</TableHead>
                      <TableHead className="text-right">Corrected</TableHead>
                      <TableHead className="text-right">Delta</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {changedDiffs.flatMap((d) =>
                      d.rows
                        .filter((r) => Math.abs(r.delta) >= 0.01)
                        .map((r, i) => (
                          <TableRow
                            key={`${d.order_id}-${i}`}
                            data-testid={`repair-diff-row-${d.order_number}-${i}`}
                          >
                            <TableCell className="font-mono text-xs">
                              {d.order_number}
                            </TableCell>
                            <TableCell className="text-sm">
                              {r.service_name || "—"}
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {fmtIDR(r.current_earned)}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {fmtIDR(r.projected_earned)}
                            </TableCell>
                            <TableCell
                              className={`text-right font-medium ${r.delta < 0 ? "text-rose-700" : "text-emerald-700"}`}
                            >
                              {r.delta > 0 ? "+" : ""}
                              {fmtIDR(r.delta)}
                            </TableCell>
                          </TableRow>
                        ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {applied && (
          <div
            className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900"
            data-testid="repair-apply-result"
          >
            <div className="font-semibold flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Fix applied to {windowBounds.label}
            </div>
            <div className="mt-1 text-emerald-800">
              {applied.orders_touched} order{applied.orders_touched === 1 ? "" : "s"} corrected
              {" · "}
              {applied.earns_rewritten} earn row{applied.earns_rewritten === 1 ? "" : "s"} rewritten
              {applied.deductions_replayed
                ? ` · ${applied.deductions_replayed} refund deduction${applied.deductions_replayed === 1 ? "" : "s"} replayed`
                : ""}
              {applied.deductions_skipped
                ? ` · ${applied.deductions_skipped} deduction${applied.deductions_skipped === 1 ? "" : "s"} skipped`
                : ""}
            </div>
          </div>
        )}

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent data-testid="repair-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Apply fix to {windowBounds.label}?
              </AlertDialogTitle>
              <AlertDialogDescription>
                This rewrites {preview?.orders_with_changes || 0} order
                {preview?.orders_with_changes === 1 ? "" : "s"} in the commission
                ledger using the current price-mode-aware formula. Refund
                deductions are automatically replayed against the new earn
                rows. Net ledger delta:{" "}
                <strong
                  className={preview?.total_delta < 0 ? "text-rose-700" : "text-emerald-700"}
                >
                  {fmtIDR(preview?.total_delta || 0)}
                </strong>
                . This cannot be undone, but you can re-run Preview afterwards
                to verify the result.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="repair-confirm-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={applyFix}
                data-testid="repair-confirm-apply"
              >
                Apply fix now
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
